import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addMinutes, parse, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { createMeeting, listMeetings } from '../../api/meetings/meetings';
import TimeSlotGrid from '../../manual-booking/components/booking/steps/TimeSlotGrid';
import {
  USA_TIMEZONES,
  getDefaultTimezone,
  getTimezoneDisplayLabel,
} from '../utils/timezone';
import '../../components/appointments/styles/AppointmentDetailsPageLayout.css';
import '../styles/MeetingDetailsPage.css';

const DEFAULT_DURATION_MINUTES = 60;
const PRESET_DURATIONS = [30, 45, 60, 90, 120];
const MIN_CUSTOM_DURATION = 15;
const MAX_CUSTOM_DURATION = 480;

const MeetingCreatePage = () => {
  const navigate = useNavigate();
  const { companyData } = useAuth();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 600);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 600px)');
    const handle = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, []);

  const [form, setForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    title: '',
    date: '',
    time: '',
    durationMinutes: DEFAULT_DURATION_MINUTES,
    meetingType: 'one_on_one',
    location: '',
    videoConferencingLink: '',
    timezone: getDefaultTimezone(),
    notes: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [extraClients, setExtraClients] = useState([]);
  const [isCustomDuration, setIsCustomDuration] = useState(false);

  const handleChange = (field) => (event) => {
    const value = field === 'durationMinutes'
      ? Number(event.target.value || DEFAULT_DURATION_MINUTES)
      : event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const durationSelectValue = isCustomDuration ? 'custom' : form.durationMinutes;

  const handleDurationSelectChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setIsCustomDuration(true);
      setForm((prev) => ({
        ...prev,
        durationMinutes: PRESET_DURATIONS.includes(prev.durationMinutes) ? DEFAULT_DURATION_MINUTES : prev.durationMinutes,
      }));
      return;
    }
    setIsCustomDuration(false);
    const minutes = Number(value) || DEFAULT_DURATION_MINUTES;
    setForm((prev) => ({ ...prev, durationMinutes: minutes }));
  };

  const handleCustomDurationChange = (event) => {
    const raw = event.target.value === '' ? '' : Number(event.target.value);
    if (raw !== '' && (Number.isNaN(raw) || raw < 0)) return;
    setForm((prev) => ({ ...prev, durationMinutes: raw }));
  };

  const getEffectiveDurationMinutes = () => {
    const raw = Number(form.durationMinutes);
    const value = (raw > 0 ? raw : DEFAULT_DURATION_MINUTES);
    return Math.min(MAX_CUSTOM_DURATION, Math.max(MIN_CUSTOM_DURATION, value));
  };

  const handleExtraClientChange = (index, field, value) => {
    setExtraClients((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return next;
    });
  };

  const handleAddExtraClient = () => {
    setExtraClients((prev) => [...prev, { name: '', email: '' }]);
  };

  const handleRemoveExtraClient = (index) => {
    setExtraClients((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePhoneChange = (event) => {
    const raw = event.target.value || '';
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    const formatted = digits.length <= 3
      ? (digits ? `(${digits}` : '')
      : digits.length <= 6
        ? `(${digits.slice(0, 3)}) ${digits.slice(3)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    setForm((prev) => ({ ...prev, clientPhone: formatted }));
  };

  const handleDateChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      date: value,
    }));

    if (value) {
      setSelectedDate(new Date(`${value}T00:00:00`));
      setSelectedTime(null);
      setForm((prev) => ({
        ...prev,
        time: '',
      }));
    } else {
      setSelectedDate(null);
      setSelectedTime(null);
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    const iso = time.toISOString();
    const datePart = iso.slice(0, 10);
    const timePart = iso.slice(11, 16);

    setForm((prev) => ({
      ...prev,
      date: datePart,
      time: timePart,
    }));
  };

  const timeSlots = useMemo(() => {
    if (!selectedDate) return [];

    const baseDate = new Date(selectedDate);
    const dayKey = baseDate
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase();

    const companyHoursForDay =
      companyData?.settings?.companyHours?.[dayKey] || null;

    let openTime;
    let closeTime;

    if (companyHoursForDay?.isOpen && companyHoursForDay.open && companyHoursForDay.close) {
      try {
        openTime = parse(
          companyHoursForDay.open.toLowerCase(),
          'h:mm aa',
          baseDate
        );
        closeTime = parse(
          companyHoursForDay.close.toLowerCase(),
          'h:mm aa',
          baseDate
        );
      } catch {
        openTime = new Date(baseDate);
        openTime.setHours(9, 0, 0, 0);
        closeTime = new Date(baseDate);
        closeTime.setHours(17, 0, 0, 0);
      }
    } else {
      openTime = new Date(baseDate);
      openTime.setHours(9, 0, 0, 0);
      closeTime = new Date(baseDate);
      closeTime.setHours(17, 0, 0, 0);
    }

    const slots = [];
    let current = new Date(openTime);
    while (current < closeTime) {
      slots.push(new Date(current));
      current = addMinutes(current, 15);
    }

    if (!bookedTimes.length) return slots;

    const durationMinutes = getEffectiveDurationMinutes();

    return slots.filter((slot) => {
      const slotStart = slot.getTime();
      const slotEnd = addMinutes(slot, durationMinutes).getTime();

      const overlaps = bookedTimes.some(
        (m) => slotStart < m.end && slotEnd > m.start
      );

      return !overlaps;
    });
  }, [selectedDate, companyData, bookedTimes, form.durationMinutes]);

  useEffect(() => {
    if (!companyData?.id || !selectedDate) {
      setBookedTimes([]);
      return;
    }

    const loadMeetingsForDay = async () => {
      try {
        const dayStart = startOfDay(selectedDate);
        const dayEnd = endOfDay(selectedDate);

        const dayMeetings = await listMeetings({
          companyId: companyData.id,
          from: dayStart,
          to: dayEnd,
          archived: false,
        });

        const intervals = dayMeetings
          .map((data) => {
            if (!data.dateTime) return null;
            try {
              const dt = data.dateTime instanceof Date ? data.dateTime : new Date(data.dateTime);
              if (!dt || isNaN(dt.getTime())) return null;
              const status = String(data.status || '').toLowerCase();
              if (status === 'cancelled') return null;

              const meetingDuration =
                (typeof data.duration === 'number' && data.duration > 0
                  ? data.duration
                  : DEFAULT_DURATION_MINUTES);

              const startMs = dt.getTime();
              const endMs = addMinutes(dt, meetingDuration).getTime();

              return { start: startMs, end: endMs };
            } catch {
              return null;
            }
          })
          .filter((iv) => iv != null);

        setBookedTimes(intervals);
      } catch {
        setBookedTimes([]);
      }
    };

    loadMeetingsForDay();
  }, [companyData?.id, selectedDate]);

  const buildDateTime = () => {
    if (!selectedTime) return null;
    try {
      return new Date(selectedTime);
    } catch {
      return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!companyData?.id) {
      setError('Company data is missing. Please try again.');
      return;
    }

    if (!form.clientName.trim() || !form.clientEmail.trim()) {
      setError('Client name and email are required.');
      return;
    }

    if (!form.title.trim()) {
      setError('Meeting title is required.');
      return;
    }

    if (!selectedDate || !selectedTime) {
      setError('Please choose a date and time slot.');
      return;
    }

    const dateTime = buildDateTime();
    if (!dateTime || Number.isNaN(dateTime.getTime())) {
      setError('The selected date or time is invalid.');
      return;
    }

    const duration = getEffectiveDurationMinutes();
    const endTime = addMinutes(dateTime, duration);

    setSubmitting(true);
    try {
      const companyId = companyData.id;
      const dayStart = startOfDay(selectedDate);
      const dayEnd = endOfDay(selectedDate);
      const existingMeetings = await listMeetings({
        companyId,
        from: dayStart,
        to: dayEnd,
        archived: false,
      });

      const hasConflict = existingMeetings.some((data) => {
        if (!data.dateTime) return false;
        const status = String(data.status || '').toLowerCase();
        if (status === 'cancelled') return false;

        try {
          const existingStart = data.dateTime instanceof Date ? data.dateTime : new Date(data.dateTime);
          if (!existingStart || isNaN(existingStart.getTime())) return false;

          const existingDuration =
            (typeof data.duration === 'number' && data.duration > 0
              ? data.duration
              : DEFAULT_DURATION_MINUTES);
          const existingEnd = addMinutes(existingStart, existingDuration);

          const startMs = dateTime.getTime();
          const endMs = endTime.getTime();
          const existingStartMs = existingStart.getTime();
          const existingEndMs = existingEnd.getTime();

          return startMs < existingEndMs && endMs > existingStartMs;
        } catch {
          return false;
        }
      });

      if (hasConflict) {
        setError('There is already a meeting that overlaps this time. Please choose another time slot.');
        setSubmitting(false);
        return;
      }

      const cleanAdditionalClients = extraClients
        .map((c) => ({
          name: c.name?.trim() || '',
          email: c.email?.trim() || '',
        }))
        .filter((c) => c.name || c.email);

      await createMeeting({
        companyId,
        meeting: {
          type: 'meeting',
          title: form.title.trim(),
          notes: form.notes || '',
          meetingType: form.meetingType || 'one_on_one',
          location: (form.location || '').trim(),
          videoConferencingLink: (form.videoConferencingLink || '').trim(),
          timezone: form.timezone || getDefaultTimezone(),
          clientName: form.clientName.trim(),
          clientEmail: form.clientEmail.trim(),
          clientPhone: form.clientPhone.trim(),
          additionalClients: cleanAdditionalClients,
          dateTime,
          endTime,
          duration,
          status: 'pending',
        },
      });

      navigate('/app/overview/meetings');
    } catch (submitError) {
      console.error('Failed to create meeting:', submitError);
      setError('Failed to create meeting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <div className="meeting-details-page">
      <div className="meeting-details-page__main">
        <button type="button" className="meeting-details-page__back-link" onClick={handleBack}>
          ← Back
        </button>

        <form onSubmit={handleSubmit} noValidate className="adp-layout meeting-form">
          <div className="adp-layout__grid adp-layout__grid--meeting-create">
            {/* Meeting details - first in DOM for mobile (shown above Clients via order); desktop order puts Clients left */}
            <div className="adp-layout__right-stack">
              <div className="adp-layout__card">
                <span className="adp-layout__card-title">Meeting details</span>
                <div className="adp-layout__card-body">
                  <div className="adp-layout__detail-section">
                    <span className="adp-layout__section-title">Meeting title<span className="adp-layout__required" aria-hidden="true"> *</span></span>
                    <div className="adp-layout__row">
                      <input
                        type="text"
                        className="meeting-form__input"
                        placeholder="e.g. Strategy review, Intake meeting"
                        value={form.title}
                        onChange={handleChange('title')}
                        required
                      />
                    </div>
                  </div>
                  <div className="adp-layout__detail-section">
                    <span className="adp-layout__section-title">Meeting type</span>
                    <div className="adp-layout__row">
                      <select
                        className="meeting-form__select"
                        value={form.meetingType}
                        onChange={handleChange('meetingType')}
                      >
                        <option value="one_on_one">One on One</option>
                        <option value="group">Group meeting</option>
                      </select>
                    </div>
                  </div>
                  <div className="adp-layout__detail-section">
                    <span className="adp-layout__section-title">Location (optional)</span>
                    <div className="adp-layout__row">
                      <input
                        type="text"
                        className="meeting-form__input"
                        placeholder="e.g. Office, Conference Room A"
                        value={form.location}
                        onChange={handleChange('location')}
                      />
                    </div>
                  </div>
                  <div className="adp-layout__detail-section">
                    <span className="adp-layout__section-title">Video conferencing (optional)</span>
                    <div className="adp-layout__row">
                      <input
                        type="url"
                        className="meeting-form__input"
                        placeholder="Zoom, Google Meet, or Teams link"
                        value={form.videoConferencingLink}
                        onChange={handleChange('videoConferencingLink')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Clients (primary + additional in one card) */}
            <div className="adp-layout__card adp-layout__card--contact" style={{ alignSelf: 'start' }}>
              <span className="adp-layout__card-title">Clients</span>
              <div className="adp-layout__card-body">
                {/* Primary contact row */}
                <div className="adp-layout__detail-section">
                  <span className="adp-layout__section-title">Primary contact<span className="adp-layout__required" aria-hidden="true"> *</span></span>
                  <div className="adp-layout__row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      className="meeting-form__input"
                      placeholder="Name"
                      value={form.clientName}
                      onChange={handleChange('clientName')}
                      required
                    />
                    <input
                      type="email"
                      className="meeting-form__input"
                      placeholder="Email"
                      value={form.clientEmail}
                      onChange={handleChange('clientEmail')}
                      required
                    />
                    <input
                      type="text"
                      className="meeting-form__input meeting-form__input--phone"
                      placeholder="(555) 555-5555"
                      value={form.clientPhone}
                      onChange={handlePhoneChange}
                    />
                  </div>
                </div>
                {/* Additional clients - only shown after "Add another client" */}
                {extraClients.map((client, index) => (
                  <div key={index} className="adp-layout__row meeting-form__row" style={{ alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <input
                      type="text"
                      className="meeting-form__input"
                      placeholder="Name"
                      value={client.name}
                      onChange={(e) => handleExtraClientChange(index, 'name', e.target.value)}
                    />
                    <input
                      type="email"
                      className="meeting-form__input"
                      placeholder="Email"
                      value={client.email}
                      onChange={(e) => handleExtraClientChange(index, 'email', e.target.value)}
                    />
                    <button
                      type="button"
                      className="adp-layout__btn"
                      style={{ padding: '6px 12px', fontSize: '0.75rem', flexShrink: 0 }}
                      onClick={() => handleRemoveExtraClient(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <div className="adp-layout__row" style={{ marginTop: 8, justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="adp-layout__complete-link"
                    onClick={handleAddExtraClient}
                    disabled={submitting}
                  >
                    Add another client
                  </button>
                </div>
              </div>
            </div>

            {/* Date & time */}
            <div className="adp-layout__card adp-layout__card--full">
              <span className="adp-layout__card-title">Date & time</span>
              <div className="adp-layout__card-body">
                <div className="adp-layout__detail-section">
                  <span className="adp-layout__section-title">Duration</span>
                  <div className="adp-layout__row">
                    <select
                      className="meeting-form__select"
                      value={durationSelectValue}
                      onChange={handleDurationSelectChange}
                    >
                      {PRESET_DURATIONS.map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {minutes} min
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  {durationSelectValue === 'custom' && (
                    <div className="adp-layout__row" style={{ marginTop: 8 }}>
                      <input
                        type="number"
                        className="meeting-form__input"
                        min={MIN_CUSTOM_DURATION}
                        max={MAX_CUSTOM_DURATION}
                        step={5}
                        placeholder={`${MIN_CUSTOM_DURATION}–${MAX_CUSTOM_DURATION} min`}
                        value={form.durationMinutes === '' ? '' : form.durationMinutes}
                        onChange={handleCustomDurationChange}
                      />
                      <p className="adp-layout__text adp-layout__text--secondary" style={{ margin: '4px 0 0', fontSize: '0.75rem' }}>
                        {MIN_CUSTOM_DURATION}–{MAX_CUSTOM_DURATION} minutes
                      </p>
                    </div>
                  )}
                </div>
                <div className="adp-layout__detail-section">
                  <span className="adp-layout__section-title">Date<span className="adp-layout__required" aria-hidden="true"> *</span></span>
                  <div className="adp-layout__row">
                    <input
                      type="date"
                      className="meeting-form__input"
                      value={form.date}
                      onChange={handleDateChange}
                      required
                    />
                  </div>
                </div>
                <div className="adp-layout__detail-section">
                  <span className="adp-layout__section-title">Timezone</span>
                  <div className="adp-layout__row">
                    <select
                      className="meeting-form__select"
                      value={form.timezone}
                      onChange={handleChange('timezone')}
                    >
                      {USA_TIMEZONES.map((tz) => (
                        <option key={tz.id} value={tz.id}>
                          {getTimezoneDisplayLabel(tz.id)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {selectedDate ? (
                  <div className="adp-layout__detail-section">
                    <span className="adp-layout__section-title">Select a time slot<span className="adp-layout__required" aria-hidden="true"> *</span></span>
                    <div className="adp-layout__row" style={{ display: 'block', marginBottom: 0 }}>
                      <TimeSlotGrid
                        timeSlots={timeSlots}
                        selectedTime={selectedTime}
                        onTimeSelect={handleTimeSelect}
                        isMobile={isMobile}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="adp-layout__text adp-layout__text--secondary">
                    Choose a date first to see available time slots.
                  </p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="adp-layout__card adp-layout__card--full">
              <span className="adp-layout__card-title">Notes</span>
              <div className="adp-layout__card-body">
                <div className="adp-layout__row">
                  <textarea
                    className="meeting-form__textarea"
                    placeholder="Add any notes for this meeting…"
                    value={form.notes}
                    onChange={handleChange('notes')}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="meeting-details-page__error" style={{ marginTop: 16 }}>
              <p>{error}</p>
            </div>
          )}

          <div className="adp-layout__actions">
            <div className="adp-layout__actions-inner">
              <button
                type="submit"
                className="adp-layout__btn adp-layout__btn--success"
                disabled={submitting}
              >
                {submitting ? 'Creating…' : 'Create meeting'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeetingCreatePage;
